<template>
  <div class="program-tab">
    <section class="panel">
      <div class="panel-header">
        <div>
          <h3>Program status</h3>
          <p>Rollout switches come from server environment variables and are read-only here.</p>
        </div>
        <el-button size="small" icon="el-icon-refresh" :loading="statusLoading" @click="fetchStatus">Refresh</el-button>
      </div>

      <div v-loading="statusLoading" class="status-body">
        <template v-if="status">
          <el-descriptions :column="3" size="small" border>
            <el-descriptions-item label="Intake mode">
              <el-tag size="small" :type="status.intake_mode === 'invite_only' ? 'success' : 'info'">{{ intakeLabel(status.intake_mode) }}</el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="Referral capture">
              <el-tag size="small" :type="switchTag(status.capture_enabled)">{{ switchLabel(status.capture_enabled) }}</el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="Commission earning">
              <el-tag size="small" :type="switchTag(status.earning_enabled)">{{ switchLabel(status.earning_enabled) }}</el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="Subscription discounts">
              <el-tag size="small" :type="switchTag(status.subscription_discounts_enabled)">{{ switchLabel(status.subscription_discounts_enabled) }}</el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="Stripe Connect">
              <el-tag size="small" :type="switchTag(status.connect_enabled)">{{ switchLabel(status.connect_enabled) }}</el-tag>
              <span class="muted inline-note">{{ connectCountries }}</span>
            </el-descriptions-item>
            <el-descriptions-item label="Connect dispatch">
              <el-tag size="small" :type="switchTag(status.dispatch_enabled)">{{ switchLabel(status.dispatch_enabled) }}</el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="Manual payouts">
              <el-tag size="small" :type="switchTag(status.manual_payout_enabled)">{{ switchLabel(status.manual_payout_enabled) }}</el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="Fee source">{{ status.fee_source || 'Not set' }}</el-descriptions-item>
            <el-descriptions-item label="Stripe mode">
              <el-tag size="small" :type="status.test_mode ? 'warning' : 'success'">{{ status.test_mode ? 'Test mode' : 'Live' }}</el-tag>
            </el-descriptions-item>
          </el-descriptions>
        </template>
        <div v-else-if="!statusLoading" class="empty">Program status is unavailable.</div>
      </div>
    </section>

    <el-row :gutter="16">
      <el-col :xs="24" :lg="12">
        <section class="panel">
          <div class="panel-header">
            <div>
              <h3>Intake mode</h3>
              <p>Invite only: admins create one-time invitation links. Closed: no new invitations or applications.</p>
            </div>
          </div>
          <el-form label-position="top" size="small" @submit.native.prevent>
            <el-form-item label="New mode">
              <el-radio-group v-model="intakeForm.mode">
                <el-radio-button label="invite_only">Invite only</el-radio-button>
                <el-radio-button label="closed">Closed</el-radio-button>
              </el-radio-group>
            </el-form-item>
            <el-form-item label="Reason" required>
              <el-input
                v-model="intakeForm.reason"
                type="textarea"
                :rows="2"
                maxlength="500"
                show-word-limit
                placeholder="Required: recorded in the affiliate audit log"
              />
            </el-form-item>
            <el-button
              type="primary"
              :loading="isPending('intake')"
              :disabled="anyPending || !intakeForm.reason.trim() || (status && status.intake_mode === intakeForm.mode)"
              @click="updateIntake"
            >
              Update intake mode
            </el-button>
            <span v-if="status && status.intake_mode === intakeForm.mode" class="muted inline-note">Already {{ intakeLabel(intakeForm.mode).toLowerCase() }}.</span>
          </el-form>
        </section>
      </el-col>
      <el-col :xs="24" :lg="12">
        <section class="panel">
          <div class="panel-header">
            <div>
              <h3>Setup</h3>
              <p>Run once before opening intake. Both actions are safe to repeat.</p>
            </div>
          </div>
          <div class="setup-row">
            <div>
              <div class="strong">Ensure database indexes</div>
              <div class="muted">Creates the affiliate collections' indexes (idempotent migration).</div>
            </div>
            <el-button size="small" :loading="isPending('indexes')" :disabled="anyPending" @click="ensureIndexes">Ensure indexes</el-button>
          </div>
          <div class="setup-row">
            <div>
              <div class="strong">Seed launch defaults</div>
              <div class="muted">Publishes the default global and per-type policies only where none is published.</div>
            </div>
            <el-button size="small" :loading="isPending('seed')" :disabled="anyPending" @click="seedDefaults">Seed defaults</el-button>
          </div>
          <el-alert
            v-if="seedResult"
            :title="seedResult.length ? `Created: ${seedResult.join(', ')}` : 'Nothing created: every default scope already has a published policy.'"
            type="info"
            show-icon
            :closable="false"
          />
        </section>
      </el-col>
    </el-row>

    <section class="panel">
      <div class="panel-header">
        <div>
          <h3>Partner terms documents</h3>
          <p>Applicants accept the latest published version. Published text is immutable; publish a new version to change it.</p>
        </div>
        <div>
          <el-button size="small" icon="el-icon-refresh" :loading="termsLoading" @click="fetchTerms">Refresh</el-button>
          <el-button size="small" type="primary" icon="el-icon-plus" :disabled="anyPending" @click="openPublish">Publish new version</el-button>
        </div>
      </div>

      <el-table v-loading="termsLoading" :data="terms" border size="small" style="width: 100%" empty-text="No terms documents published yet">
        <el-table-column label="Version" width="90">
          <template slot-scope="scope"><span class="strong">v{{ scope.row.version }}</span></template>
        </el-table-column>
        <el-table-column label="Status" width="110">
          <template slot-scope="scope">
            <el-tag size="small" :type="scope.row.status === 'published' ? 'success' : 'info'">{{ scope.row.status || '-' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Document" min-width="220">
          <template slot-scope="scope">
            <div class="mono">{{ idOf(scope.row) }}</div>
            <div class="muted mono" :title="scope.row.content_sha256">sha256 {{ shortId(scope.row.content_sha256, 16) }}</div>
          </template>
        </el-table-column>
        <el-table-column label="Published" width="150">
          <template slot-scope="scope">{{ formatTime(scope.row.published_at) }}</template>
        </el-table-column>
        <el-table-column label="Reason" min-width="200">
          <template slot-scope="scope">
            <div>{{ scope.row.reason || '-' }}</div>
            <div v-if="scope.row.status === 'retired'" class="muted">
              Retired {{ formatTime(scope.row.retired_at) }}<span v-if="scope.row.retirement_reason">: {{ scope.row.retirement_reason }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="Actions" width="110" fixed="right">
          <template slot-scope="scope">
            <el-button
              v-if="scope.row.status === 'published'"
              size="mini"
              type="danger"
              plain
              :loading="isPending(`retire:${idOf(scope.row)}`)"
              :disabled="anyPending"
              @click="retireTerms(scope.row)"
            >
              Retire
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </section>

    <el-dialog title="Publish partner terms" :visible.sync="publishVisible" width="720px" :close-on-click-modal="false">
      <el-alert
        title="Publishing makes this text the version new applicants must accept. It cannot be edited afterwards."
        type="warning"
        show-icon
        :closable="false"
        class="dialog-alert"
      />
      <el-form label-position="top" size="small" @submit.native.prevent>
        <el-form-item label="Full terms text" required>
          <el-input
            v-model="publishForm.content"
            type="textarea"
            :rows="14"
            maxlength="200000"
            show-word-limit
            placeholder="Paste the complete partner terms exactly as applicants should read them."
          />
        </el-form-item>
        <el-form-item label="Reason" required>
          <el-input v-model="publishForm.reason" maxlength="500" show-word-limit placeholder="Required: e.g. Launch terms, legal review 2026-09" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="publishVisible = false">Cancel</el-button>
        <el-button
          type="primary"
          :loading="isPending('publishTerms')"
          :disabled="anyPending || !publishForm.content.trim() || !publishForm.reason.trim()"
          @click="publishTerms"
        >
          Publish version
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  ensureAffiliateIndexes,
  getAffiliateProgramStatus,
  listAffiliateTermsDocuments,
  publishAffiliateTermsDocument,
  retireAffiliateTermsDocument,
  seedAffiliateDefaults,
  setAffiliateIntakeMode
} from '@/apis/lfAdmin'
import affiliateMixin from './affiliateMixin'

export default {
  name: 'AffiliateProgramTab',
  mixins: [affiliateMixin],
  data() {
    return {
      statusLoading: false,
      status: null,
      intakeForm: {
        mode: 'invite_only',
        reason: ''
      },
      seedResult: null,
      termsLoading: false,
      terms: [],
      publishVisible: false,
      publishForm: {
        content: '',
        reason: ''
      }
    }
  },
  computed: {
    connectCountries() {
      const countries = (this.status && this.status.connect_countries) || []
      return countries.length ? countries.join(', ') : 'No countries'
    }
  },
  mounted() {
    this.fetchStatus()
    this.fetchTerms()
  },
  methods: {
    intakeLabel(mode) {
      if (mode === 'invite_only') return 'Invite only'
      if (mode === 'closed') return 'Closed'
      return mode || '-'
    },
    switchLabel(value) {
      return value ? 'On' : 'Off'
    },
    switchTag(value) {
      return value ? 'success' : 'info'
    },
    async fetchStatus() {
      this.statusLoading = true
      const data = await this.loadData(() => getAffiliateProgramStatus(), 'Failed to load program status')
      if (data !== undefined) {
        this.status = data
        if (data && data.intake_mode) this.intakeForm.mode = data.intake_mode === 'closed' ? 'invite_only' : 'closed'
      }
      this.statusLoading = false
    },
    async fetchTerms() {
      this.termsLoading = true
      const data = await this.loadData(() => listAffiliateTermsDocuments(), 'Failed to load terms documents')
      if (data !== undefined) this.terms = Array.isArray(data) ? data : []
      this.termsLoading = false
    },
    async updateIntake() {
      const mode = this.intakeForm.mode
      const reason = this.intakeForm.reason.trim()
      if (!reason) return
      const confirmed = await this.confirmAction({
        title: 'Change intake mode',
        lines: [
          `Set affiliate intake to "${this.intakeLabel(mode)}"?`,
          mode === 'closed'
            ? 'New invitations and applications will be refused. Existing partners are not affected.'
            : 'Admins can create invitation links and invited users can apply.',
          `Reason: ${reason}`
        ],
        confirmText: 'Change mode'
      })
      if (!confirmed) return
      const result = await this.runMutation('intake', () => setAffiliateIntakeMode({ mode, reason }), {
        success: 'Intake mode updated',
        failure: 'Failed to update intake mode'
      })
      if (result) {
        this.intakeForm.reason = ''
        this.fetchStatus()
      }
    },
    async ensureIndexes() {
      const confirmed = await this.confirmAction({
        title: 'Ensure indexes',
        lines: ['Create or verify all affiliate database indexes? This is idempotent and safe to repeat.'],
        confirmText: 'Ensure indexes',
        type: 'info'
      })
      if (!confirmed) return
      await this.runMutation('indexes', () => ensureAffiliateIndexes(), {
        success: 'Affiliate indexes ready',
        failure: 'Failed to ensure indexes'
      })
    },
    async seedDefaults() {
      const confirmed = await this.confirmAction({
        title: 'Seed launch defaults',
        lines: [
          'Publish the launch-default policies (global rates and per-type buyer offers) for every scope that has no published policy yet?',
          'Scopes that already have a published policy are left unchanged. Seeded policies take effect immediately.'
        ],
        confirmText: 'Seed defaults'
      })
      if (!confirmed) return
      const result = await this.runMutation('seed', () => seedAffiliateDefaults(), {
        success: 'Launch defaults checked',
        failure: 'Failed to seed defaults'
      })
      if (result) this.seedResult = (result.data && Array.isArray(result.data.created)) ? result.data.created : []
    },
    openPublish() {
      this.publishForm = { content: '', reason: '' }
      this.publishVisible = true
    },
    async publishTerms() {
      const content = this.publishForm.content
      const reason = this.publishForm.reason.trim()
      if (!content.trim() || !reason) return
      const latest = this.terms.reduce((max, row) => Math.max(max, Number(row.version || 0)), 0)
      const confirmed = await this.confirmAction({
        title: 'Publish terms version',
        lines: [
          `Publish ${content.length.toLocaleString('en-US')} characters as partner terms v${latest + 1}?`,
          'New applicants must accept this version. Earlier published versions stay published until you retire them.',
          `Reason: ${reason}`
        ],
        confirmText: 'Publish'
      })
      if (!confirmed) return
      const result = await this.runMutation('publishTerms', () => publishAffiliateTermsDocument({ content, reason }), {
        failure: 'Failed to publish terms'
      })
      if (result) {
        const version = result.data && result.data.version
        this.$message.success(version ? `Terms v${version} published` : 'Terms published')
        this.publishVisible = false
        this.fetchTerms()
      }
    },
    async retireTerms(row) {
      const id = this.idOf(row)
      const reason = await this.confirmWithReason({
        title: 'Retire terms version',
        lines: [
          `Retire partner terms v${row.version}?`,
          'Applicants can no longer accept it. Publish a new version so new applications can continue.'
        ],
        confirmText: 'Retire'
      })
      if (reason === null) return
      const result = await this.runMutation(`retire:${id}`, () => retireAffiliateTermsDocument({ id, reason }), {
        success: 'Terms version retired',
        failure: 'Failed to retire terms'
      })
      if (result) this.fetchTerms()
    }
  }
}
</script>

<style scoped>
.panel {
  padding: 16px;
  margin-bottom: 16px;
  background: #fff;
  border: 1px solid #ebeef5;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
}

.panel-header h3 {
  margin: 0;
  color: #303133;
  font-size: 16px;
}

.panel-header p {
  margin: 4px 0 0;
  color: #909399;
  font-size: 13px;
}

.status-body {
  min-height: 60px;
}

.setup-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #f2f3f5;
}

.setup-row:last-of-type {
  margin-bottom: 12px;
}

.strong {
  color: #303133;
  font-weight: 600;
}

.muted {
  color: #909399;
  font-size: 12px;
}

.inline-note {
  margin-left: 8px;
}

.mono {
  font-family: Menlo, Consolas, monospace;
  font-size: 12px;
  word-break: break-all;
}

.empty {
  padding: 20px;
  color: #909399;
  text-align: center;
}

.dialog-alert {
  margin-bottom: 12px;
}
</style>
